import * as React from "react"
import { MultiPick, type MultiPickOption, type MultiPickProps } from "./multi-pick"
import { cn } from "../../lib/utils"

export interface AsyncMultiPickProps extends Omit<MultiPickProps, "options"> {
  /**
   * `preload` – fetch all options once when opened, then filter client-side.
   * `search`  – fetch options on each search query (debounced).
   */
  mode: "preload" | "search"

  /**
   * Fetch options from the backend.
   * - In `preload` mode, called once with no query.
   * - In `search` mode, called with the current search string (supports search by id for preselected IDs on mount).
   */
  fetchOptions: (query?: string) => Promise<MultiPickOption[]>

  /** Debounce delay in ms for `search` mode. Default: 300. */
  debounceMs?: number

  /** Minimum characters before triggering a search in `search` mode. Default: 1. */
  minSearchLength?: number
}

export function AsyncMultiPick({
  mode,
  fetchOptions,
  debounceMs = 300,
  minSearchLength = 1,
  onOpenChange,
  onSearchChange,
  className,
  ...multiPickProps
}: AsyncMultiPickProps) {
  const { selected } = multiPickProps
  const [options, setOptions] = React.useState<MultiPickOption[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasPreloaded, setHasPreloaded] = React.useState(false)

  const cacheRef = React.useRef<Map<string, MultiPickOption[]>>(new Map())

  const fetchWithCache = React.useCallback(
    async (query?: string) => {
      const key = query ?? "__no_query__"
      const cached = cacheRef.current.get(key)
      if (cached) return cached

      const result = await fetchOptions(query)
      cacheRef.current.set(key, result)
      return result
    },
    [fetchOptions],
  )

  // Cache selected items so they remain visible even when not in current search results
  const selectedCacheRef = React.useRef<Map<string, MultiPickOption>>(new Map())

  // Preload on mount when we have preselected IDs (e.g. from LS) so they display as checked with labels
  React.useEffect(() => {
    if (selected.length === 0) return

    if (mode === "preload" && !hasPreloaded) {
      setIsLoading(true)
      fetchWithCache()
        .then((result) => {
          setOptions(result)
          setHasPreloaded(true)
        })
        .finally(() => setIsLoading(false))
      return
    }

    // Search mode: endpoint supports search by id – fetch each selected id so they display with labels
    if (mode === "search") {
      const missing = selected.filter((id) => !selectedCacheRef.current.has(id))
      if (missing.length === 0) return
      setIsLoading(true)
      Promise.all(missing.map((id) => fetchWithCache(id)))
        .then((arrays) => {
          const resolved = arrays.flat()
          for (const opt of resolved) {
            if (selected.includes(opt.value)) {
              selectedCacheRef.current.set(opt.value, opt)
            }
          }
          setOptions((prev) => {
            const byVal = new Map(prev.map((o) => [o.value, o]))
            for (const o of resolved) byVal.set(o.value, o)
            return Array.from(byVal.values())
          })
        })
        .catch(() => undefined)
        .finally(() => setIsLoading(false))
    }
  }, [mode, selected, hasPreloaded, fetchWithCache])

  // Update cache when we get options that match selected values
  React.useEffect(() => {
    for (const opt of options) {
      if (selected.includes(opt.value)) {
        selectedCacheRef.current.set(opt.value, opt)
      }
    }
  }, [options, selected])

  // Remove deselected items from cache
  React.useEffect(() => {
    for (const key of selectedCacheRef.current.keys()) {
      if (!selected.includes(key)) {
        selectedCacheRef.current.delete(key)
      }
    }
  }, [selected])

  // Merge fetched options with cached selected items
  const mergedOptions = React.useMemo(() => {
    const optionMap = new Map(options.map((o) => [o.value, o]))
    for (const [key, opt] of selectedCacheRef.current) {
      if (!optionMap.has(key)) {
        optionMap.set(key, opt)
      }
    }
    return Array.from(optionMap.values())
  }, [options])

  // -- Preload mode --
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (mode !== "preload" || !open || hasPreloaded) return
      setIsLoading(true)
      fetchWithCache()
        .then((result) => {
          setOptions(result)
          setHasPreloaded(true)
        })
        .finally(() => setIsLoading(false))
      onOpenChange?.(open)
    },
    [mode, hasPreloaded, fetchWithCache, onOpenChange],
  )

  // -- Search mode: debounced --
  const searchIdRef = React.useRef(0)
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleSearchChange = React.useCallback(
    (query: string) => {
      onSearchChange?.(query)
      if (mode !== "search") return

      if (timerRef.current) clearTimeout(timerRef.current)

      if (query.trim().length < minSearchLength) {
        // Keep cached selected items visible but clear search results
        setOptions([])
        return
      }

      const id = ++searchIdRef.current
      timerRef.current = setTimeout(() => {
        setIsLoading(true)
        fetchWithCache(query.trim())
          .then((result) => {
            if (id === searchIdRef.current) setOptions(result)
          })
          .finally(() => {
            if (id === searchIdRef.current) setIsLoading(false)
          })
      }, debounceMs)
    },
    [mode, fetchWithCache, debounceMs, minSearchLength, onSearchChange],
  )

  // Cleanup timer
  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <div className={cn("relative", className)}>
      <MultiPick
        {...multiPickProps}
        options={mergedOptions}
        disableClientFilter={mode === "search"}
        loading={isLoading}
        onOpenChange={handleOpenChange}
        onSearchChange={mode === "search" ? handleSearchChange : onSearchChange}
      />
    </div>
  )
}
